import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useMounted } from '@vueuse/core';

import { useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { usePromotion } from '@/helpers/composables/usePromotion';
import { appleQualityTvCountries, primeQualityTvCountries } from '@/components/buybox/QualityTvRow/data';

import { OfferLocation } from '@/helpers/offer-helper';
import type { Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';

// AMAZON_OS_FREE_TRIAL_EXP_2
import {
	AmazonOSFreeTrialExp2Name,
	AmazonOSFreeTrialExp2Variants,
} from '@/components/experiments/AmazonOSFreeTrialExp2';
import AmazonOSFreeTrialExpPromotionRowNew from '@/components/experiments/AmazonOSFreeTrialExp2/AmazonOSFreeTrialExpPromotionRowNew.vue';
// AMAZON_OS_FREE_TRIAL_EXP_2

import {
	AQTVTitleDetailPlacementsName,
	AQTVTitleDetailPlacementsVariants,
} from '@/components/experiments/AQTVTitleDetailPlacememts';

export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxPromotionRowNew',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
};



const PromotionRowNew = () => import('@/components/buybox-new/PromotionRowNew.vue');
const BuyBoxQualityTVNew = () => import('@/components/buybox-new/BuyBoxQualityTVNew.vue');

const AppleQTVRowContainer = () =>
	import('@/components/experiments/AQTVTitleDetailPlacememts/components/AppleQTVRowContainer.vue');



const { isLoggedIn } = useUserStore();
const { isArbitrating } = useArbitrage();
const { country } = useLanguageStore();
const { activeVariants, activeVariantsWithControlGroup } = useExperimentStore();
const isMounted = useMounted();
const appleQualityTvExpVariant = computed(
	() => activeVariants.value[AQTVTitleDetailPlacementsName] as AQTVTitleDetailPlacementsVariants
);

const appleQualityTvExpActive = computed(
	() => !!appleQualityTvExpVariant.value && props.title.__typename === 'Movie' && isMounted.value
);

const { promotionOffer } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const showPromotionRowWhenArbitrating = computed(
	() => isArbitrating.value && 350 === promotionOffer.value?.package?.packageId
);

const isQualityTvCountry = computed(() => {
	if (!promotionOffer.value) return false;

	if (promotionOffer.value.package.packageId === 350 && appleQualityTvCountries.includes(country.value)) return true;

	return (
		[9, 119].includes(promotionOffer.value.package.packageId) &&
		primeQualityTvCountries.includes(country.value) &&
		props.title.__typename === 'Show'
	);
});

const showQualityTv = computed(() => {
	return (
		!isLoggedIn.value &&
		promotionOffer.value != null &&
		promotionOffer.value.id === 'PROMOTION_OFFER' &&
		isQualityTvCountry.value &&
		(!isMounted.value || (isMounted.value && !showPromotionRowWhenArbitrating.value))
	);
});

// AMAZON_OS_FREE_TRIAL_EXP_2
const isAmazonOSFreeTrialExp = computed(() => {
	return !!isMounted.value && amazonOSFreeTrialExpVariant.value;
});

const amazonOSFreeTrialExpVariant = computed(() => {
	return activeVariantsWithControlGroup.value[AmazonOSFreeTrialExp2Name] as AmazonOSFreeTrialExp2Variants;
});

const hasAmazonOffer = computed(() => {
	return props.offers.some(offer => offer.package.packageId === 9);
});

const displayAmazonOSFreeTrialExpPromotionRow = computed(() => {
	return (
		isMounted.value &&
		props.showFreeTrial &&
		isAmazonOSFreeTrialExp.value &&
		hasAmazonOffer.value &&
		country.value === 'US'
	);
});
// AMAZON_OS_FREE_TRIAL_EXP_2

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,props, PromotionRowNew, BuyBoxQualityTVNew, AppleQTVRowContainer, emit, isLoggedIn, isArbitrating, country, activeVariants, activeVariantsWithControlGroup, isMounted, appleQualityTvExpVariant, appleQualityTvExpActive, promotionOffer, showPromotionRowWhenArbitrating, isQualityTvCountry, showQualityTv, isAmazonOSFreeTrialExp, amazonOSFreeTrialExpVariant, hasAmazonOffer, displayAmazonOSFreeTrialExpPromotionRow, onClickOut, AmazonOSFreeTrialExpPromotionRowNew, AQTVTitleDetailPlacementsName }
}

})